/**
 * Limites do protocolo. Vivem aqui porque o cliente TEM de os conhecer:
 * validar no browser evita um round-trip para receber um 400 previsível,
 * e o servidor volta a validar sempre (o cliente nunca é fonte de verdade).
 */
export const Limits = {
    // Liberal de propósito: o handle é a IDENTIDADE que a pessoa escreve, por isso
    // aceita maiúsculas, acentos e espaços ("João Silva" é um username válido). Só
    // se barram os caracteres que partem o sistema — `@` e `#` (sintaxe de menção/
    // canal) e os de controlo. A UNICIDADE não é sensível a isto: o servidor guarda
    // uma chave normalizada (minúsculas, espaços colapsados) para "João" e "joão"
    // colidirem e ninguém se poder fazer passar por outro.
    username: { min: 2, max: 32, pattern: /^[^\p{Cc}@#]+$/u },
    displayName: { min: 1, max: 48 },
    password: { min: 8, max: 256 },
    bio: { max: 512 },
    statusText: { max: 128 },
    serverName: { min: 2, max: 64 },
    serverDescription: { max: 1024 },
    channelName: { min: 1, max: 64, pattern: /^[^\s#@][^#@]*$/ },
    channelTopic: { max: 1024 },
    roleName: { min: 1, max: 48 },
    nickname: { max: 48 },
    /**
     * Ao contrário do `username`, isto é apertado de propósito: o nome vive
     * dentro do token `<:nome:id>` no meio do texto de uma mensagem. Um `:`, um
     * `>` ou um espaço lá dentro tornam o token ambíguo de ler — e a mesma
     * mensagem passaria a render-se de maneiras diferentes conforme o parser.
     * Letras, dígitos e `_` chegam para tudo o que as pessoas escrevem.
     */
    emojiName: { min: 2, max: 32, pattern: /^[a-zA-Z0-9_]+$/ },
    messageContent: { max: 4000 },
    attachmentsPerMessage: 10,
    embedsPerMessage: 5,
    reactionsPerMessage: 24,
    mentionsPerMessage: 32,
    /** Sem contar com o próprio: 9 + eu = 10 numa conversa de grupo. */
    groupDmRecipients: 9,
    /** 8 caracteres base62 ≈ 2.2e14 combinações: colidir é mais raro do que o retry. */
    inviteCodeLength: 8,
    /** 0 = sem limite. */
    inviteMaxUses: { min: 0, max: 1000 },
    /** 0 = nunca expira; o tecto são 30 dias. */
    inviteMaxAgeSeconds: { min: 0, max: 2_592_000 },
    invitesPerServer: 500,
    rolesPerServer: 128,
    channelsPerServer: 512,
    /**
     * O tecto existe pelo Ready: os emojis vão todos no payload inicial de cada
     * ligação, e 200 × ~120 bytes são 24 KB por servidor — aceitável. Sem limite,
     * um servidor com dez mil emojis atrasava o arranque de toda a gente lá
     * dentro, incluindo de quem nunca usa nenhum.
     */
    emojisPerServer: 200,
    /**
     * Tópicos por canal de texto. Não contam para o `channelsPerServer` — um canal
     * movimentado não pode gastar o orçamento de canais REAIS do servidor — mas
     * também não podem ser infinitos: um tópico é uma linha de `channels` como as
     * outras, e a lista de um canal carrega-se de uma vez quando se entra nele.
     *
     * Não há arquivo nem expiração de propósito (era mais estado a sincronizar por
     * um problema que 100 não tem). Chegado ao tecto, apaga-se um: são conversas
     * de dez mensagens, não canais.
     */
    threadsPerChannel: 100,
    rolesPerMember: 32,
    pinsPerChannel: 100,
    bulkDeleteMax: 100,
    messagesPerFetch: { default: 50, max: 100 },
    membersPerFetch: { default: 100, max: 200 },
    /**
     * Procura: 25 por página, e o termo tem de ter 2 caracteres. Com um só, a
     * consulta varre metade do servidor para devolver ruído — e o índice de texto
     * integral nem sequer indexa palavras de uma letra.
     */
    searchResultsPerFetch: { default: 25, max: 50 },
    searchQuery: { min: 2, max: 200 },
    /**
     * Silêncio temporário, em minutos a contar de AGORA — é o cliente que diz
     * quanto tempo, nunca a data final. Um relógio adiantado no telemóvel dava um
     * silêncio já expirado no instante em que era criado, e um atrasado dava um
     * silêncio que nunca acabava; assim quem carimba a hora é quem a mede.
     * Acima de uma semana já é "para sempre" — que se diz mandando `null`.
     */
    muteMinutes: { min: 1, max: 10080 },
    slowmodeSeconds: { min: 0, max: 21600 },
    voiceUserLimit: { min: 0, max: 99 },
    voiceBitrate: { min: 8000, max: 256000, default: 64000 },
};
/**
 * Rate limits: { pedidos, janela em ms }.
 * O `send` é generoso por burst (colar três linhas seguidas é normal) e
 * apertado no sustentado. O `login` é agressivo de propósito.
 */
export const RateLimits = {
    login: { limit: 5, windowMs: 60_000 },
    /**
     * Generoso de propósito: cada tentativa FALHADA (typo na password, email já
     * usado) também consome uma unidade, e atrás de um túnel/NAT vários amigos
     * partilham o mesmo IP — um limite apertado trancava toda a gente por uma hora
     * (o "2000s" que o amigo apanhou). 30 por 10 min chega para um grupo a
     * registar-se ao mesmo tempo e ainda trava um bot; a espera máxima é 10 min,
     * não uma hora. Registo aberto continua atrás de ALLOW_OPEN_REGISTRATION.
     */
    register: { limit: 30, windowMs: 600_000 },
    messageSend: { limit: 10, windowMs: 10_000 },
    messageEdit: { limit: 10, windowMs: 10_000 },
    reactionAdd: { limit: 20, windowMs: 10_000 },
    fileUpload: { limit: 10, windowMs: 60_000 },
    serverCreate: { limit: 5, windowMs: 3_600_000 },
    channelCreate: { limit: 20, windowMs: 60_000 },
    inviteCreate: { limit: 10, windowMs: 60_000 },
    /** Pedidos de amizade: o alvo é escolhido por username, logo é o caminho por
     *  onde se enumera quem existe. Apertado de propósito. */
    relationshipWrite: { limit: 20, windowMs: 60_000 },
    dmCreate: { limit: 20, windowMs: 60_000 },
    voiceJoin: { limit: 10, windowMs: 60_000 },
    /** Uma procura custa um GIN scan; escrever na caixa dispara uma por tecla. */
    messageSearch: { limit: 30, windowMs: 60_000 },
    /** Silenciar é um upsert numa linha só — folgado, mas não infinito. */
    muteWrite: { limit: 60, windowMs: 60_000 },
    /**
     * Cada emoji criado prende um ficheiro no armazenamento para sempre; o limite
     * é sobre o gesto de administração, não sobre leitura (listar é livre).
     */
    emojiWrite: { limit: 20, windowMs: 60_000 },
    /**
     * Abrir um tópico é um direito de MEMBRO (`CREATE_THREADS`), ao contrário de
     * criar um canal — por isso tem limite próprio, e mais apertado do que o
     * `channelCreate`, que só quem manda no servidor consegue gastar.
     */
    threadCreate: { limit: 10, windowMs: 60_000 },
    gatewayIdentify: { limit: 5, windowMs: 60_000 },
    /**
     * Bater ponto no relógio do escritório. Um dia de trabalho são quatro batidas;
     * dez por minuto chega para quem se engana e ainda trava um script a martelar
     * a porta de serviço do painel admin.
     */
    pontoWrite: { limit: 10, windowMs: 60_000 },
    /** Genérico, aplicado a tudo o que não tem regra própria. */
    global: { limit: 120, windowMs: 60_000 },
};
/**
 * Tipos aceites em upload. Whitelist — nunca blacklist.
 *
 * Sem SVG, e é deliberado: um SVG é um documento com `<script>` lá dentro. Os
 * anexos são servidos na MESMA origem do site (a ponte serve `/s3` ao lado da
 * app) e a lista de mensagens embrulha cada imagem num `<a href target=_blank>`
 * — abrir um SVG assim é navegação de topo, e o script correria com a sessão de
 * quem carregou. Nenhum dos outros tipos tem essa propriedade: um PDF abre no
 * visualizador, isolado do DOM. Voltar a pô-lo aqui exige primeiro servir os
 * ficheiros noutro domínio.
 */
export const ALLOWED_UPLOAD_TYPES = new Set([
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
    "image/avif",
    "video/mp4",
    "video/webm",
    "video/quicktime",
    "audio/mpeg",
    "audio/ogg",
    "audio/wav",
    "audio/webm",
    "application/pdf",
    "application/zip",
    "application/json",
    "text/plain",
    "text/markdown",
    "text/csv",
]);
/** Extensões executáveis: bloqueadas mesmo que o content-type diga outra coisa. */
export const BLOCKED_EXTENSIONS = new Set([
    "exe",
    "msi",
    "bat",
    "cmd",
    "com",
    "scr",
    "pif",
    "vbs",
    "js",
    "jse",
    "ws",
    "wsf",
    "wsh",
    "ps1",
    "psm1",
    "jar",
    "app",
    "dmg",
    "pkg",
    "deb",
    "rpm",
    "sh",
    "bash",
    "apk",
    "dll",
    "so",
    "lnk",
    "reg",
    "hta",
]);
export function isUploadAllowed(filename, contentType) {
    const ext = filename.split(".").pop()?.toLowerCase() ?? "";
    if (BLOCKED_EXTENSIONS.has(ext)) {
        return { ok: false, reason: `Extensão .${ext} bloqueada por segurança.` };
    }
    const mime = contentType.split(";")[0]?.trim().toLowerCase() ?? "";
    if (!ALLOWED_UPLOAD_TYPES.has(mime)) {
        return { ok: false, reason: `Tipo de ficheiro não suportado: ${mime || "desconhecido"}.` };
    }
    return { ok: true };
}
//# sourceMappingURL=limits.js.map